"use client";

import { motion } from "framer-motion";
import { Globe, ArrowRight } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function AppleLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

function AndroidLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.4154.4154 0 00-.5676.1521L17.117 8.93a11.6309 11.6309 0 00-5.1168-1.1792 11.5636 11.5636 0 00-5.1053 1.1758L4.8463 5.4617a.416.416 0 00-.5677-.1521.4154.4154 0 00-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589.3432 18.6617h23.3136c0-4.0028-2.3457-7.475-5.7805-9.3403" />
    </svg>
  );
}

export function AppAvailabilitySection() {
  return (
    <Section background="gray">
      <Container size="xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h2 className="text-display-1 text-ink mb-4">
            Available for Customers
          </h2>
          <p className="text-lg text-muted">
            Your products reach customers through our iOS app, with Android and
            web coming soon.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="p-6 h-full">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-ink rounded-xl">
                  <AppleLogo className="w-6 h-6 text-page" />
                </div>
                <div>
                  <h3 className="font-semibold text-ink">iOS App</h3>
                  <Badge variant="success">Available Now</Badge>
                </div>
              </div>
              <p className="text-muted mb-4 text-sm">
                Customers can discover and purchase products directly from the
                Marketplace app on iOS.
              </p>
              <Button
                href="https://apps.apple.com/app/id6478976454"
                external
                className="w-full"
                variant="marketplace"
              >
                Download on App Store
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="p-6 h-full">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-brand-green-soft rounded-xl">
                  <AndroidLogo className="w-6 h-6 text-brand-green" />
                </div>
                <div>
                  <h3 className="font-semibold text-ink">Android App</h3>
                  <Badge variant="default">Coming Soon</Badge>
                </div>
              </div>
              <p className="text-muted mb-4 text-sm">
                Android shoppers will soon be able to browse deals and buy
                products right from their phones.
              </p>
              <Button variant="secondary" className="w-full" disabled>
                Stay Tuned
              </Button>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="p-6 h-full">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-brand-blue-soft rounded-xl">
                  <Globe className="w-6 h-6 text-brand-blue" />
                </div>
                <div>
                  <h3 className="font-semibold text-ink">Web App</h3>
                  <Badge variant="default">Coming Soon</Badge>
                </div>
              </div>
              <p className="text-muted mb-4 text-sm">
                We&apos;re building a web version so customers can shop from any
                device, anywhere in the world.
              </p>
              <Button variant="secondary" className="w-full" disabled>
                Stay Tuned
              </Button>
            </Card>
          </motion.div>
        </div>
      </Container>
    </Section>
  );
}
